package br.com.vvrefeicoes.portal.domain.model

/** Situação da folha líquida do mês: só fica completa com adiantamento e pagamento importados. */
enum class RhNetPayrollStatus {
    COMPLETE,
    AWAITING_ADIANTAMENTO,
    AWAITING_PAGAMENTO,
    MISSING,
}

/** Consolidado de um mês do RH. [absenteeismPercent] e [netPayroll] são `null` sem planilha importada. */
data class RhSummaryMonth(
    val period: MonthPeriod,
    val active: Int,
    val admitted: Int,
    val dismissed: Int,
    val turnoverPercent: Double,
    val absenteeismPercent: Double?,
    val overtime: Double,
    val bonus: Double,
    val productivity: Double,
    val totalCost: Double,
    val netPayroll: Double?,
    val netPayrollStatus: RhNetPayrollStatus,
    val costsImported: Boolean = true,
)

data class RhSummaryUnit(
    val name: String,
    val employees: Int,
    val admitted: Int,
    val dismissed: Int,
    val turnoverPercent: Double,
    val absenteeismPercent: Double?,
    val overtime: Double,
    val nightShift: Double,
    val bonus: Double,
    val costAllowance: Double,
    val salaryDifference: Double,
    val insalubrity: Double,
    val hazardousDuty: Double,
    val productivity: Double,
    val totalCost: Double,
    val advance: Double,
    val payment: Double,
    val netPayroll: Double,
)

data class RhRanking(
    val name: String,
    val total: Double,
    val employees: Int,
)

/** Resumo executivo: [monthly] traz os últimos meses com dados e [current] a competência selecionada. */
data class RhSummary(
    val directors: List<String>,
    val monthly: List<RhSummaryMonth>,
    val current: RhSummaryMonth?,
    val activeVariationPercent: Double?,
    val units: List<RhSummaryUnit>,
    val netPayrollByManager: List<RhRanking>,
    val netPayrollByDirector: List<RhRanking>,
    val netPayrollByGestora: List<RhRanking>,
)

data class RhCountItem(
    val label: String,
    val count: Int,
)

data class RhDismissed(
    val total: Int,
    val distinctJobs: Int,
    val byReason: List<RhCountItem>,
    val bySex: List<RhCountItem>,
    val topJobTitles: List<RhCountItem>,
    val topUnits: List<RhCountItem>,
)

data class RhVacancy(
    val rmpId: Long,
    val status: String?,
    val hrStatus: String?,
    val unitName: String?,
    val functionName: String?,
    val shift: String?,
    val contractType: String?,
    val requesterName: String?,
    val updatedAt: String?,
)

/** Vagas em aberto: requisições da RMP ainda não concluídas nem rejeitadas. */
data class RhVacancies(
    val total: Int,
    val byStatus: List<RhCountItem>,
    val byUnit: List<RhCountItem>,
    val byFunction: List<RhCountItem>,
    val vacancies: List<RhVacancy>,
)

/** Horas extras e demais verbas variáveis de um mês. [overtimeSharePercent] é a fatia das horas extras nos custos variáveis. */
data class RhOvertimeMonth(
    val period: MonthPeriod,
    val employees: Int,
    val employeesWithOvertime: Int,
    val overtime50: Double,
    val overtime100: Double,
    val overtimeDsr: Double,
    val overtimeTotal: Double,
    val averagePerEmployee: Double,
    val overtimeSharePercent: Double,
    val productivity: Double,
    val nightShift: Double,
    val bonus: Double,
    val costAllowance: Double,
    val salaryDifference: Double,
    val insalubrity: Double,
    val hazardousDuty: Double,
    val totalCost: Double,
)

/** Horas extras de um grupo (unidade, função ou gerente). [sharePercent] é a fatia no total do mês. */
data class RhOvertimeGroup(
    val name: String,
    val employees: Int,
    val employeesWithOvertime: Int,
    val overtime50: Double,
    val overtime100: Double,
    val overtimeDsr: Double,
    val overtimeTotal: Double,
    val sharePercent: Double,
)

data class RhOvertime(
    val monthly: List<RhOvertimeMonth>,
    val current: RhOvertimeMonth?,
    val byUnit: List<RhOvertimeGroup>,
    val byJobTitle: List<RhOvertimeGroup>,
    val byManager: List<RhOvertimeGroup>,
)
